import React, { useCallback, useEffect, useState } from "react";
import { Calendar, momentLocalizer, Views, type View } from "react-big-calendar";
import moment from "moment";
import "moment/locale/pt-br";
import "react-big-calendar/lib/css/react-big-calendar.css";
import Select from "react-select";
import secureLocalStorage from "react-secure-storage";
import { useNavigate } from "react-router";
import { AxiosResponse } from "axios";

import Header from "../../components/Header";
import Loading from "../../components/Loading";
import ModalPrincipal from "../../components/ModalAlert/modal";
import CalendarCustomFieldFilters, {
  type PortalCustomField,
} from "../../components/CalendarCustomFieldFilters";
import { FaChevronDown, FaPlus, FaSlidersH } from "react-icons/fa";
import { Link } from "react-router-dom";
import api from "../../Services/api";
import "../../components/GridUsuario/index.css";
import "../Direcionamento/task-page.css";
import "./index.css";

moment.locale("pt-br");

const localizer = momentLocalizer(moment);

const EXCLUDED_SITUATIONS = ["Concluída", "Cancelada"];

const AGENT_COLORS = [
  "#3962ac",
  "#0076BE",
  "#22c55e",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#f59e0b",
  "#6366f1",
];

const calendarMessages = {
  allDay: "Dia inteiro",
  previous: "Anterior",
  next: "Próximo",
  today: "Hoje",
  month: "Mês",
  week: "Semana",
  day: "Dia",
  agenda: "Agenda",
  date: "Data",
  time: "Hora",
  event: "Visita",
  noEventsInRange: "Não há visitas agendadas neste período.",
  showMore: (total: number) => `+${total} mais`,
};

interface TaskRow {
  tsk_id: number;
  tsk_active?: number;
  age_id?: number | null;
  tea_id?: number | null;
  ast_description?: string;
  loc_description?: string;
  age_login?: string;
  tae_description?: string;
  tsk_scheduleinitialdatehour?: string;
  tsk_schedulefinaldatehour?: string;
  tsk_realinitialdatehour?: string;
  tsk_realfinaldatehour?: string;
  stn_description?: string;
  tty_description?: string;
  executado?: string;
}

interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  agent: string;
  situation: string;
  local: string;
  asset: string;
  location: string;
  taskType: string;
  scheduled: string;
  executed: string;
}

interface SelectOption {
  value: string;
  label: string;
}

interface CustomFieldFilter {
  tcf_id: string;
  value: string;
}

interface CalendarFilters {
  ageId: string;
  locId: string;
  astId: string;
  ttyId: string;
  stnId: string;
  customFields: CustomFieldFilter[];
}

interface TaskCustomFieldValueRow {
  tcf_id: number;
  tcf_description: string;
  tcv_value?: string | null;
}

function loadPortalCustomFieldsFromStorage(): PortalCustomField[] {
  const pcf = secureLocalStorage.getItem("pcf");
  if (!pcf) {
    return [];
  }
  if (Array.isArray(pcf)) {
    return pcf as PortalCustomField[];
  }
  if (typeof pcf === "string") {
    try {
      const parsed = JSON.parse(pcf);
      return Array.isArray(parsed) ? (parsed as PortalCustomField[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function mergeCustomFieldDefinitions(
  fromApi: PortalCustomField[],
  fromStorage: PortalCustomField[]
): PortalCustomField[] {
  const byId = new Map<number, PortalCustomField>();

  for (const field of fromApi) {
    if (field.tcf_id != null && field.tcf_active !== 0) {
      byId.set(field.tcf_id, field);
    }
  }

  for (const field of fromStorage) {
    if (field.tcf_id != null && field.tcf_active !== 0 && !byId.has(field.tcf_id)) {
      byId.set(field.tcf_id, field);
    }
  }

  return Array.from(byId.values()).sort((a, b) =>
    (a.tcf_description || "").localeCompare(b.tcf_description || "", "pt-BR")
  );
}

function isCustomFieldsEnabled(): boolean {
  const pp = secureLocalStorage.getItem("pp");
  if (!pp || !Array.isArray(pp) || pp.length === 0) {
    return true;
  }
  const params = pp[0] as { ppr_allowcustomfields?: number };
  return params.ppr_allowcustomfields !== 0;
}

async function fetchCustomFieldDefinitions(): Promise<PortalCustomField[]> {
  const fromStorage = loadPortalCustomFieldsFromStorage();

  try {
    const ret = await api.get("taskcustomfield/listar");
    if (ret.data?.status === 200 && Array.isArray(ret.data.data)) {
      return mergeCustomFieldDefinitions(
        ret.data.data as PortalCustomField[],
        fromStorage
      );
    }
  } catch {
    /* usa storage como fallback */
  }

  return fromStorage.filter((f) => f.tcf_active !== 0);
}

function buildCustomFieldFilters(
  values: Record<string, string>
): CustomFieldFilter[] {
  return Object.entries(values)
    .filter(([, value]) => value.trim() !== "")
    .map(([tcf_id, value]) => ({ tcf_id, value: value.trim() }));
}

function parseScheduleDate(value: string | null | undefined): Date | null {
  if (!value) return null;

  const br = moment(value, "DD/MM/YYYY HH:mm:ss", true);
  if (br.isValid()) return br.toDate();

  const brShort = moment(value, "DD/MM/YYYY HH:mm", true);
  if (brShort.isValid()) return brShort.toDate();

  const iso = moment(value);
  if (iso.isValid()) return iso.toDate();

  return null;
}

function formatApiDate(d: Date): string {
  return moment(d).format("YYYY-MM-DD");
}

function formatEventDateTime(d: Date): string {
  return moment(d).format("DD/MM/YYYY HH:mm");
}

function formatScheduleLabel(
  initialRaw: string | undefined,
  finalRaw: string | undefined,
  startFallback: Date,
  endFallback: Date
): string {
  const startLabel =
    initialRaw?.trim() || formatEventDateTime(startFallback);
  const finalParsed = parseScheduleDate(finalRaw);
  if (finalParsed) {
    return `${startLabel} — ${formatEventDateTime(finalParsed)}`;
  }
  if (finalRaw?.trim()) {
    return `${startLabel} — ${finalRaw.trim()}`;
  }
  if (endFallback > startFallback) {
    return `${startLabel} — ${formatEventDateTime(endFallback)}`;
  }
  return startLabel;
}

function formatExecutedLabel(task: TaskRow): string {
  if (task.executado?.trim()) return task.executado.trim();

  const realStart = parseScheduleDate(task.tsk_realinitialdatehour);
  if (!realStart) return "—";

  const realEnd = parseScheduleDate(task.tsk_realfinaldatehour);
  if (realEnd && realEnd > realStart) {
    return `${formatEventDateTime(realStart)} — ${formatEventDateTime(realEnd)}`;
  }
  return formatEventDateTime(realStart);
}

function displayOrDash(value: string | undefined): string {
  const v = value?.trim();
  return v ? v : "—";
}

function formatIdWithPlace(taskId: number, place: string): string {
  const trimmed = place.trim();
  return trimmed ? `${taskId} - ${trimmed}` : "—";
}

function buildEventPlaceLabel(
  taskId: number,
  local: string,
  asset: string,
  taskType: string
): string {
  const place = local || asset;
  if (place) {
    return `${taskId} - ${place}`;
  }
  if (taskType) {
    return `${taskId} - ${taskType}`;
  }
  return String(taskId);
}

function agentColor(agent: string): string {
  if (!agent) return AGENT_COLORS[0];
  let hash = 0;
  for (let i = 0; i < agent.length; i++) {
    hash = agent.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AGENT_COLORS[Math.abs(hash) % AGENT_COLORS.length];
}

function hasAssignee(task: TaskRow): boolean {
  return Boolean(Number(task.age_id)) || Boolean(Number(task.tea_id));
}

function mapTasksToEvents(
  tasks: TaskRow[],
  situationFilterActive: boolean
): CalendarEvent[] {
  const events: CalendarEvent[] = [];

  for (const task of tasks) {
    const situation = (task.stn_description || "").trim();
    if (
      !situationFilterActive &&
      EXCLUDED_SITUATIONS.includes(situation)
    ) {
      continue;
    }

    const start = parseScheduleDate(task.tsk_scheduleinitialdatehour);
    if (!start) continue;

    let end = parseScheduleDate(task.tsk_schedulefinaldatehour);
    if (!end || end <= start) {
      end = moment(start).add(1, "hour").toDate();
    }

    const local = task.loc_description?.trim() || "";
    const asset = task.ast_description?.trim() || "";
    const agent =
      task.age_login?.trim() ||
      task.tae_description?.trim() ||
      "Sem técnico";
    const taskType = task.tty_description?.trim() || "";
    const placeLabel = buildEventPlaceLabel(
      task.tsk_id,
      local,
      asset,
      taskType
    );

    events.push({
      id: task.tsk_id,
      title: agent,
      start,
      end,
      agent,
      situation,
      local,
      asset,
      location: placeLabel,
      taskType,
      scheduled: formatScheduleLabel(
        task.tsk_scheduleinitialdatehour,
        task.tsk_schedulefinaldatehour,
        start,
        end
      ),
      executed: formatExecutedLabel(task),
    });
  }

  return events;
}

function getRangeForView(date: Date, view: View): { start: Date; end: Date } {
  const m = moment(date);
  if (view === Views.DAY) {
    return { start: m.clone().startOf("day").toDate(), end: m.clone().endOf("day").toDate() };
  }
  if (view === Views.WEEK) {
    return { start: m.clone().startOf("week").toDate(), end: m.clone().endOf("week").toDate() };
  }
  return { start: m.clone().startOf("month").toDate(), end: m.clone().endOf("month").toDate() };
}

const Calendario: React.FC = () => {
  const navigate = useNavigate();

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [unassignedTasks, setUnassignedTasks] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUnassigned, setIsLoadingUnassigned] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [openSummaryModal, setOpenSummaryModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [qualAlert, setQualAlert] = useState<React.ReactNode>(<></>);
  const [agentOptions, setAgentOptions] = useState<SelectOption[]>([]);
  const [localOptions, setLocalOptions] = useState<SelectOption[]>([]);
  const [assetOptions, setAssetOptions] = useState<SelectOption[]>([]);
  const [taskTypeOptions, setTaskTypeOptions] = useState<SelectOption[]>([]);
  const [situationOptions, setSituationOptions] = useState<SelectOption[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<SelectOption | null>(null);
  const [selectedLocal, setSelectedLocal] = useState<SelectOption | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<SelectOption | null>(null);
  const [selectedTaskType, setSelectedTaskType] = useState<SelectOption | null>(null);
  const [selectedSituation, setSelectedSituation] = useState<SelectOption | null>(null);
  const [portalCustomFields, setPortalCustomFields] = useState<PortalCustomField[]>(
    []
  );
  const [customFieldsAvailable, setCustomFieldsAvailable] = useState(false);
  const [customFieldsOpen, setCustomFieldsOpen] = useState(false);
  const [customFieldFilters, setCustomFieldFilters] = useState<
    Record<string, string>
  >({});
  const [modalCustomFields, setModalCustomFields] = useState<
    TaskCustomFieldValueRow[]
  >([]);
  const [loadingModalCustomFields, setLoadingModalCustomFields] = useState(false);

  const [ready, setReady] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<View>(Views.MONTH);
  const [dateRange, setDateRange] = useState(() =>
    getRangeForView(new Date(), Views.MONTH)
  );

  const fetchTasks = useCallback(
    async (range: { start: Date; end: Date }, filters: CalendarFilters) => {
      setIsLoading(true);

      const dados = [
        {
          tsk_active: "1",
          tsk_id: "",
          tea_id: "",
          age_id: filters.ageId,
          loc_id: filters.locId,
          ast_id: filters.astId,
          tsk_integrationid: "",
          stn_id: filters.stnId,
          tsk_observation: "",
          tty_id: filters.ttyId,
          crt_id: "",
          pvt_id: "",
          pla_id: "",
          startdate: formatApiDate(range.start),
          enddate: formatApiDate(range.end),
          startdateUlt: "",
          enddateUlt: "",
          search: "",
          perPage: 9999,
          page: 0,
          loc_responsibletechnician: secureLocalStorage.getItem("vw"),
          age_responsibletechnician: secureLocalStorage.getItem("iag"),
          customFields: filters.customFields,
        },
      ];

      const _user = secureLocalStorage.getItem("tpcm");
      api.defaults.headers.common["Authorization"] = _user as string;

      try {
        const ret = await api.get("task/buscartaskcalendar", {
          params: { data: dados },
        });
        const rows: TaskRow[] = ret.data?.data || [];
        setEvents(mapTasksToEvents(rows, Boolean(filters.stnId)));
      } catch {
        setEvents([]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const fetchUnassignedTasks = useCallback(
    async (range: { start: Date; end: Date }, filters: CalendarFilters) => {
      setIsLoadingUnassigned(true);

      const dados = [
        {
          tsk_active: "1",
          tsk_id: "",
          tea_id: "",
          age_id: "",
          unassigned: "1",
          loc_id: filters.locId,
          ast_id: filters.astId,
          tsk_integrationid: "",
          stn_id: filters.stnId,
          tsk_observation: "",
          tty_id: filters.ttyId,
          crt_id: "",
          pvt_id: "",
          pla_id: "",
          startdate: formatApiDate(range.start),
          enddate: formatApiDate(range.end),
          startdateUlt: "",
          enddateUlt: "",
          search: "",
          perPage: 9999,
          page: 0,
          loc_responsibletechnician: secureLocalStorage.getItem("vw"),
          age_responsibletechnician: secureLocalStorage.getItem("iag"),
          customFields: filters.customFields,
        },
      ];

      const _user = secureLocalStorage.getItem("tpcm");
      api.defaults.headers.common["Authorization"] = _user as string;

      try {
        const ret = await api.get("task/buscartaskcalendar", {
          params: { data: dados },
        });
        const rows: TaskRow[] = ret.data?.data || [];
        const onlyUnassigned = rows.filter((row) => !hasAssignee(row));
        setUnassignedTasks(mapTasksToEvents(onlyUnassigned, Boolean(filters.stnId)));
      } catch {
        setUnassignedTasks([]);
      } finally {
        setIsLoadingUnassigned(false);
      }
    },
    []
  );

  const loadFilterOptions = useCallback(async () => {
    try {
      const [agentsRes, localsRes, assetsRes, taskTypesRes, situationsRes] =
        await Promise.all([
          api.get("user/listar"),
          api.get("local/listarlocal"),
          api.get("asset/listaresset"),
          api.get("tasktype/listar"),
          api.get("tasksituation/listar"),
        ]);

      if (agentsRes.data?.status === 200 && Array.isArray(agentsRes.data.data)) {
        const agents = agentsRes.data.data.map(
          (user: { age_id: number; age_name: string }) => ({
            value: String(user.age_id),
            label: user.age_name,
          })
        );
        setAgentOptions(agents);
      } else {
        setAgentOptions([]);
      }

      if (localsRes.data?.status === 200 && Array.isArray(localsRes.data.data)) {
        const locals = localsRes.data.data.map(
          (loc: { loc_id: number; loc_description: string }) => ({
            value: String(loc.loc_id),
            label: loc.loc_description,
          })
        );
        setLocalOptions(locals);
      } else {
        setLocalOptions([]);
      }

      if (assetsRes.data?.status === 200 && Array.isArray(assetsRes.data.data)) {
        const assets = assetsRes.data.data.map(
          (ast: { ast_id: number; ast_description: string }) => ({
            value: String(ast.ast_id),
            label: ast.ast_description,
          })
        );
        setAssetOptions(assets);
      } else {
        setAssetOptions([]);
      }

      if (
        taskTypesRes.data?.status === 200 &&
        Array.isArray(taskTypesRes.data.data)
      ) {
        const types = taskTypesRes.data.data.map(
          (tty: { tty_id: number; tty_description: string }) => ({
            value: String(tty.tty_id),
            label: tty.tty_description,
          })
        );
        setTaskTypeOptions(types);
      } else {
        setTaskTypeOptions([]);
      }

      if (
        situationsRes.data?.status === 200 &&
        Array.isArray(situationsRes.data.data)
      ) {
        const situations = situationsRes.data.data.map(
          (stn: { stn_id: number; stn_description: string }) => ({
            value: String(stn.stn_id),
            label: stn.stn_description,
          })
        );
        setSituationOptions(situations);
      } else {
        setSituationOptions([]);
      }
    } catch {
      setAgentOptions([]);
      setLocalOptions([]);
      setAssetOptions([]);
      setTaskTypeOptions([]);
      setSituationOptions([]);
    }
  }, []);

  const validateToken = useCallback(async () => {
    const _user = secureLocalStorage.getItem("tpcm");
    api.defaults.headers.common["Authorization"] = _user as string;

    try {
      const ret: AxiosResponse = await api.get("/status/validar");

      if (ret.data.status === 200) {
        secureLocalStorage.setItem("t", ret.data.data);
        secureLocalStorage.setItem(
          "acp",
          ret.data.decodedToken.acp_integrationid
        );
        secureLocalStorage.setItem("n", ret.data.decodedToken.name);
        secureLocalStorage.setItem("iag", ret.data.decodedToken.id);
        secureLocalStorage.setItem("cy", ret.data.decodedToken.company);

        await loadFilterOptions();

        const fields = await fetchCustomFieldDefinitions();
        setPortalCustomFields(fields);
        setCustomFieldsAvailable(
          fields.length > 0 && isCustomFieldsEnabled()
        );
        setCustomFieldsOpen(false);

        setOpenModal(false);
        setReady(true);
        return true;
      }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number } };
      if (err.response === undefined) {
        setQualAlert(
          <div className="div-alert">
            <span className="fa-regular fa-circle-xmark ControleIconAlertcancel"></span>
            <div className="div-info-alert-atencao">
              <span className="info-alert-atencao">ATENÇÃO</span>
            </div>
            <div className="div-info-alert">
              <span className="info-alert">Sem comunicação com o servidor.</span>
            </div>
            <div className="btn-alert" onClick={fecharModalLogin}>
              <span className="btn-alert-lab">OK</span>
            </div>
          </div>
        );
      } else if (err.response?.status === 403) {
        setQualAlert(
          <div className="div-alert">
            <span className="fa-regular fa-circle-xmark ControleIconAlertcancel"></span>
            <div className="div-info-alert-atencao">
              <span className="info-alert-atencao">ATENÇÃO</span>
            </div>
            <div className="div-info-alert">
              <span className="info-alert">Token inválido ou expirado.</span>
            </div>
            <div className="btn-alert" onClick={fecharModalLogin}>
              <span className="btn-alert-lab">OK</span>
            </div>
          </div>
        );
      } else {
        setQualAlert(
          <div className="div-alert">
            <span className="fa-regular fa-circle-xmark ControleIconAlertcancel"></span>
            <div className="div-info-alert-atencao">
              <span className="info-alert-atencao">ATENÇÃO</span>
            </div>
            <div className="div-info-alert">
              <span className="info-alert">
                Não foi possível concluir sua solicitação.
              </span>
            </div>
            <div className="btn-alert" onClick={() => setOpenModal(false)}>
              <span className="btn-alert-lab">OK</span>
            </div>
          </div>
        );
      }
      setOpenModal(true);
    }
    return false;
  }, [loadFilterOptions]);

  const activeFilters: CalendarFilters = {
    ageId: selectedAgent?.value || "",
    locId: selectedLocal?.value || "",
    astId: selectedAsset?.value || "",
    ttyId: selectedTaskType?.value || "",
    stnId: selectedSituation?.value || "",
    customFields: buildCustomFieldFilters(customFieldFilters),
  };

  const handleCustomFieldChange = (tcfId: string, value: string) => {
    setCustomFieldFilters((prev) => ({ ...prev, [tcfId]: value }));
  };

  const activeCustomFilterCount = Object.values(customFieldFilters).filter(
    (value) => value.trim() !== ""
  ).length;

  function fecharModalLogin() {
    secureLocalStorage.removeItem("paramsEntidade");
    secureLocalStorage.removeItem("paramsQrcode");
    secureLocalStorage.removeItem("tpcm");
    secureLocalStorage.removeItem("acp");
    secureLocalStorage.removeItem("n");
    secureLocalStorage.removeItem("iag");
    secureLocalStorage.removeItem("aua");
    navigate("/login");
    setOpenModal(false);
  }

  useEffect(() => {
    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ready) return;
    fetchTasks(dateRange, activeFilters);
    fetchUnassignedTasks(dateRange, activeFilters);
  }, [
    ready,
    dateRange,
    selectedAgent,
    selectedLocal,
    selectedAsset,
    selectedTaskType,
    selectedSituation,
    customFieldFilters,
    fetchTasks,
    fetchUnassignedTasks,
  ]);

  const handleRangeChange = (range: Date[] | { start: Date; end: Date }) => {
    if (Array.isArray(range)) {
      if (range.length >= 2) {
        setDateRange({ start: range[0], end: range[range.length - 1] });
      }
    } else {
      setDateRange(range);
    }
  };

  const handleNavigate = (newDate: Date) => {
    setCurrentDate(newDate);
    setDateRange(getRangeForView(newDate, currentView));
  };

  const handleViewChange = (view: View) => {
    setCurrentView(view);
    setDateRange(getRangeForView(currentDate, view));
  };

  const eventStyleGetter = useCallback((event: CalendarEvent) => {
    const backgroundColor = agentColor(event.agent);
    return {
      style: {
        backgroundColor,
        borderColor: backgroundColor,
        color: "#fff",
        borderRadius: "4px",
        fontSize: "12px",
      },
    };
  }, []);

  const handleSelectEvent = async (event: CalendarEvent) => {
    setSelectedEvent(event);
    setModalCustomFields([]);
    setOpenSummaryModal(true);

    setLoadingModalCustomFields(true);
    const _user = secureLocalStorage.getItem("tpcm");
    api.defaults.headers.common["Authorization"] = _user as string;

    try {
      const ret = await api.get("taskcustomfield/listarvalor", {
        params: { id: event.id },
      });
      if (ret.data?.status === 200 && Array.isArray(ret.data.data)) {
        setModalCustomFields(ret.data.data);
      }
    } catch {
      setModalCustomFields([]);
    } finally {
      setLoadingModalCustomFields(false);
    }
  };

  const EventComponent = ({ event }: { event: CalendarEvent }) => (
    <div className="calendar-event-inner">
      <strong className="calendar-event-agent">{event.agent}</strong>
      <span className="calendar-event-place">{event.location}</span>
    </div>
  );

  return (
    <>
      <div className="chamados-container">
        <Header />

        <main className="assignment-main calendar-page-main">
          <div className="task-page calendar-page">
            <div className="grid-usuario">
              <div className="grid-usuario__hero">
                <div className="grid-usuario__hero-glow" aria-hidden />
                <div className="grid-usuario__hero-text">
                  <h2 className="grid-usuario__hero-title">Calendário de Visitas</h2>
                  <p className="grid-usuario__hero-subtitle">
                    Agendas dos técnicos — visitas pendentes e em andamento no período selecionado.
                  </p>
                </div>
                <div className="grid-usuario__hero-actions">
                  <Link to="/task/add" className="grid-usuario__hero-btn">
                    <FaPlus size={16} aria-hidden />
                    <span>Nova Tarefa</span>
                  </Link>
                </div>
              </div>

              <div className="calendar-toolbar">
                <div className="calendar-toolbar-main">
                  <div className="calendar-filters-row">
                    <div className="calendar-filter-group">
                      <label className="calendar-filter-label" htmlFor="agent-filter">
                        Técnico
                      </label>
                      <div className="calendar-filter-select">
                        <Select
                          inputId="agent-filter"
                          options={agentOptions}
                          value={selectedAgent}
                          onChange={(opt) => setSelectedAgent(opt)}
                          placeholder="Técnicos"
                          isClearable
                          classNamePrefix="calendar-select"
                        />
                      </div>
                    </div>

                    <div className="calendar-filter-group">
                      <label className="calendar-filter-label" htmlFor="local-filter">
                        Local
                      </label>
                      <div className="calendar-filter-select">
                        <Select
                          inputId="local-filter"
                          options={localOptions}
                          value={selectedLocal}
                          onChange={(opt) => setSelectedLocal(opt)}
                          placeholder="Locais"
                          isClearable
                          classNamePrefix="calendar-select"
                        />
                      </div>
                    </div>

                    <div className="calendar-filter-group">
                      <label className="calendar-filter-label" htmlFor="asset-filter">
                        Ativo
                      </label>
                      <div className="calendar-filter-select">
                        <Select
                          inputId="asset-filter"
                          options={assetOptions}
                          value={selectedAsset}
                          onChange={(opt) => setSelectedAsset(opt)}
                          placeholder="Ativos"
                          isClearable
                          classNamePrefix="calendar-select"
                        />
                      </div>
                    </div>

                    <div className="calendar-filter-group">
                      <label
                        className="calendar-filter-label"
                        htmlFor="tasktype-filter"
                      >
                        Tipo de tarefa
                      </label>
                      <div className="calendar-filter-select">
                        <Select
                          inputId="tasktype-filter"
                          options={taskTypeOptions}
                          value={selectedTaskType}
                          onChange={(opt) => setSelectedTaskType(opt)}
                          placeholder="Tipos"
                          isClearable
                          classNamePrefix="calendar-select"
                        />
                      </div>
                    </div>

                    <div className="calendar-filter-group">
                      <label
                        className="calendar-filter-label"
                        htmlFor="situation-filter"
                      >
                        Situação
                      </label>
                      <div className="calendar-filter-select">
                        <Select
                          inputId="situation-filter"
                          options={situationOptions}
                          value={selectedSituation}
                          onChange={(opt) => setSelectedSituation(opt)}
                          placeholder="exc. concluída/cancelada"
                          isClearable
                          classNamePrefix="calendar-select"
                        />
                      </div>
                    </div>

                    {customFieldsAvailable && (
                      <div className="calendar-filter-group">
                        <label className="calendar-filter-label">
                          Campos customizados
                        </label>
                        <div className="calendar-filter-select">
                          <button
                            type="button"
                            className={`calendar-custom-fields-toggle ${
                              customFieldsOpen ? "is-open" : ""
                            }`}
                            onClick={() => setCustomFieldsOpen((open) => !open)}
                            aria-expanded={customFieldsOpen}
                          >
                            <FaSlidersH className="calendar-custom-fields-toggle-icon" />
                            <span>
                              {customFieldsOpen ? "Ocultar filtros" : "Exibir filtros"}
                            </span>
                            {activeCustomFilterCount > 0 && (
                              <span className="calendar-custom-fields-badge">
                                {activeCustomFilterCount}
                              </span>
                            )}
                            <FaChevronDown className="calendar-custom-fields-chevron" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {customFieldsAvailable && customFieldsOpen && (
                  <div className="calendar-custom-fields-panel">
                    <CalendarCustomFieldFilters
                      embedded
                      fields={portalCustomFields}
                      values={customFieldFilters}
                      onChange={handleCustomFieldChange}
                    />
                  </div>
                )}
              </div>

              <div className="calendar-layout">
                <div className="calendar-container">
                  {isLoading ? (
                    <div className="calendar-loading">
                      <Loading />
                    </div>
                  ) : (
                    <Calendar
                      localizer={localizer}
                      events={events}
                      startAccessor="start"
                      endAccessor="end"
                      titleAccessor="title"
                      defaultView={Views.MONTH}
                      view={currentView}
                      onView={handleViewChange}
                      views={[Views.MONTH, Views.WEEK, Views.DAY]}
                      date={currentDate}
                      onNavigate={handleNavigate}
                      onRangeChange={handleRangeChange}
                      messages={calendarMessages}
                      eventPropGetter={eventStyleGetter}
                      components={{ event: EventComponent }}
                      onSelectEvent={handleSelectEvent}
                      style={{ height: "calc(100vh - 320px)", minHeight: 520 }}
                    />
                  )}
                </div>

                <aside className="calendar-unassigned-sidebar">
                  <div className="calendar-unassigned-header">
                    <div>
                      <h2 className="calendar-unassigned-title">Sem responsável</h2>
                      <p className="calendar-unassigned-subtitle">
                        Tarefas sem técnico e sem equipe
                      </p>
                    </div>
                    {!isLoadingUnassigned && (
                      <span className="calendar-unassigned-count">
                        {unassignedTasks.length}
                      </span>
                    )}
                  </div>

                  {isLoadingUnassigned ? (
                    <div className="calendar-unassigned-loading">
                      <Loading />
                    </div>
                  ) : unassignedTasks.length === 0 ? (
                    <p className="calendar-unassigned-empty">
                      Nenhuma tarefa sem responsável neste período.
                    </p>
                  ) : (
                    <ul className="calendar-unassigned-list">
                      {unassignedTasks.map((task) => (
                        <li key={task.id}>
                          <button
                            type="button"
                            className="calendar-unassigned-item"
                            onClick={() => handleSelectEvent(task)}
                          >
                            <div className="calendar-unassigned-item-top">
                              <strong className="calendar-unassigned-item-id">
                                #{task.id}
                              </strong>
                              <span className="calendar-unassigned-item-situation">
                                {task.situation || "—"}
                              </span>
                            </div>
                            <span className="calendar-unassigned-item-place">
                              {task.local || task.asset || task.taskType || "—"}
                            </span>
                            {task.taskType && (task.local || task.asset) ? (
                              <span className="calendar-unassigned-item-type">
                                {task.taskType}
                              </span>
                            ) : null}
                            <span className="calendar-unassigned-item-date">
                              {task.scheduled}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </aside>
              </div>
            </div>
          </div>
        </main>
      </div>

      <ModalPrincipal
        isOpen={openModal}
        setModalOpen={() => setOpenModal(!openModal)}
      >
        <div className="container-modal-alert">{qualAlert}</div>
      </ModalPrincipal>

      <ModalPrincipal
        isOpen={openSummaryModal}
        setModalOpen={() => setOpenSummaryModal(false)}
        className="calendar-summary-dialog"
      >
        {selectedEvent && (
          <div className="calendar-summary-modal">
            <div className="calendar-summary-header">
              <div className="calendar-summary-heading">
                <h2 className="calendar-summary-title">{selectedEvent.agent}</h2>
                <span
                  className="calendar-summary-badge"
                  style={{
                    backgroundColor: agentColor(selectedEvent.agent),
                  }}
                >
                  {selectedEvent.situation}
                </span>
              </div>
              <button
                type="button"
                className="calendar-summary-close"
                onClick={() => setOpenSummaryModal(false)}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>

            <div className="calendar-summary-body">
              <p className="calendar-summary-subtitle">
                Tarefa #{selectedEvent.id}
              </p>

              <dl className="calendar-summary-list">
                <div className="calendar-summary-item">
                  <dt>Onde</dt>
                  <dd>{formatIdWithPlace(selectedEvent.id, selectedEvent.local)}</dd>
                </div>
                <div className="calendar-summary-item">
                  <dt>Ativo</dt>
                  <dd>{formatIdWithPlace(selectedEvent.id, selectedEvent.asset)}</dd>
                </div>
                <div className="calendar-summary-item">
                  <dt>Tipo de tarefa</dt>
                  <dd>{displayOrDash(selectedEvent.taskType)}</dd>
                </div>
                <div className="calendar-summary-item">
                  <dt>Previsto</dt>
                  <dd>{selectedEvent.scheduled}</dd>
                </div>
                <div className="calendar-summary-item">
                  <dt>Executado</dt>
                  <dd>{selectedEvent.executed}</dd>
                </div>
              </dl>

              {(portalCustomFields.length > 0 || modalCustomFields.length > 0) && (
                <div className="calendar-summary-custom-section">
                  <h3 className="calendar-summary-custom-title">
                    Campos customizados
                  </h3>
                  {loadingModalCustomFields ? (
                    <p className="calendar-summary-custom-loading">Carregando...</p>
                  ) : modalCustomFields.length > 0 ? (
                    <dl className="calendar-summary-list calendar-summary-custom-list">
                      {modalCustomFields.map((field) => (
                        <div
                          key={field.tcf_id}
                          className="calendar-summary-item"
                        >
                          <dt>{field.tcf_description}</dt>
                          <dd>{displayOrDash(field.tcv_value ?? undefined)}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p className="calendar-summary-custom-empty">—</p>
                  )}
                </div>
              )}
            </div>

            <div className="calendar-summary-actions">
              <button
                type="button"
                className="btn-alert btn-alert-secondary"
                onClick={() => setOpenSummaryModal(false)}
              >
                <span className="btn-alert-lab">Fechar</span>
              </button>
              <button
                type="button"
                className="btn-alert"
                onClick={() => {
                  setOpenSummaryModal(false);
                  navigate(`/task/edit/${selectedEvent.id}`, {
                    state: { from: "/calendar" },
                  });
                }}
              >
                <span className="btn-alert-lab">Editar</span>
              </button>
            </div>
          </div>
        )}
      </ModalPrincipal>
    </>
  );
};

export default Calendario;
